import React, { useMemo, useState, useEffect } from 'react';
import { IngredientTraceCode } from '../../../types/ingredientTraceCode';
import { UsageRecord } from '../../../types/usageRecord';
import { Supplier } from '../../../types/supplier';
import { ShipmentEntry } from '../../../types/shipment';
import { Recipe } from '../../../types/recipe';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { scrollElementToCenter, scrollToFieldAndHighlight, getItcFieldElementId } from '../../../utils/scrollUtils';

interface TrackingEntryRowProps {
  entry: IngredientTraceCode;
  suppliers: Supplier[];
  shipmentEntries: ShipmentEntry[];
  fillings: Recipe[];
  components: Recipe[];
  isEditMode: boolean;
  onChange: (updatedEntry: IngredientTraceCode) => void;
  onDelete: () => void;
  usageCount?: number;
  isUsageLoading?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
  isHighlighted?: boolean;
  targetField?: string;
  isFlagged?: boolean;
  usageRecords?: UsageRecord[];
  usageFetched?: boolean;
}

// Parse combined quantity field (e.g., "5 lbs" -> { number: "5", unit: "lbs" })
const parseQuantity = (quantityUsed?: string): { number: string; unit: string } => {
  if (!quantityUsed) return { number: '', unit: '' };
  const parts = quantityUsed.trim().split(/\s+/);
  if (parts.length === 1) {
    // Just a number, no unit
    return { number: parts[0], unit: '' };
  }
  // First part is number, rest is unit
  return { number: parts[0], unit: parts.slice(1).join(' ') };
};

// Parse combined finished product field (e.g., "Meat Buns - Frozen" -> { product: "Meat Buns", modifier: "Frozen" })
const parseFinishedProduct = (finishedProduct: string): { product: string; modifier: string } => {
  if (!finishedProduct) return { product: '', modifier: '' };
  const parts = finishedProduct.split(' - ');
  if (parts.length === 1) {
    return { product: parts[0], modifier: '' };
  }
  return { product: parts[0], modifier: parts.slice(1).join(' - ') };
};

export const TrackingEntryRow: React.FC<TrackingEntryRowProps> = ({
  entry,
  suppliers,
  shipmentEntries,
  fillings,
  components,
  isEditMode,
  onChange,
  onDelete,
  usageCount,
  isUsageLoading,
  onVisibilityChange,
  isHighlighted = false,
  targetField,
  isFlagged,
  usageRecords,
  usageFetched
}) => {
  const { openTraceability } = useTraceability();

  // Parse combined fields into separate values for UI
  const [quantityNumber, setQuantityNumber] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('');
  const [productName, setProductName] = useState('');
  const [productModifier, setProductModifier] = useState('');

  // Intersection observer for lazy loading
  const [rowRef, isVisible] = useIntersectionObserver<HTMLTableRowElement>({
    threshold: 0.1,
    rootMargin: '50px',
    freezeOnceVisible: true
  });

  // Highlight state for deep-linking
  const [isHighlightActive, setIsHighlightActive] = useState(isHighlighted);

  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      // Delay scroll so layout settles after section expansion
      const scrollTimer = setTimeout(() => {
        if (rowRef.current) scrollElementToCenter(rowRef.current);
      }, 300);
      const timer = setTimeout(() => setIsHighlightActive(false), 3000);
      return () => { clearTimeout(scrollTimer); clearTimeout(timer); };
    }
  }, [isHighlighted]);

  // Field-level deep-linking: scroll to and highlight specific field
  useEffect(() => {
    if (!targetField) return;
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(getItcFieldElementId(entry.id, targetField), {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetField, entry.id, isFlagged]);

  // Notify parent when visibility changes
  useEffect(() => {
    if (isVisible && onVisibilityChange) {
      onVisibilityChange(true);
    }
  }, [isVisible, onVisibilityChange]);

  // Update local state when entry changes
  useEffect(() => {
    // Use quantityUnit from entry if available, otherwise parse from quantityUsed for backwards compatibility
    if (entry.quantityUnit !== undefined) {
      setQuantityNumber(entry.quantityUsed || '');
      setQuantityUnit(entry.quantityUnit || '');
    } else {
      const parsedQty = parseQuantity(entry.quantityUsed);
      setQuantityNumber(parsedQty.number);
      setQuantityUnit(parsedQty.unit);
    }

    const parsedProduct = parseFinishedProduct(entry.finishedProduct || '');
    setProductName(parsedProduct.product);
    setProductModifier(parsedProduct.modifier);
  }, [entry.quantityUsed, entry.quantityUnit, entry.finishedProduct]);

  // Get lot numbers for selected supplier
  const availableLotNumbers = useMemo(() => {
    if (!entry.supplierId) return [];
    const lots = shipmentEntries
      .filter(se => se.supplierId === entry.supplierId)
      .map(se => se.lotNumber)
      .filter((lot, index, self) => lot && self.indexOf(lot) === index); // Unique, non-empty values
    return lots;
  }, [entry.supplierId, shipmentEntries]);

  // Get all recipes/fillings for dropdown
  const allProducts = useMemo(() => {
    return [...fillings, ...components];
  }, [fillings, components]);

  // Get supplier name
  const supplierName = useMemo(() => {
    return suppliers.find(s => s.id === entry.supplierId)?.supplierName || '-';
  }, [suppliers, entry.supplierId]);

  // Generate internal trace code in format: YYYYMMDD-[SupplierCode]-[LotNumber]
  const generateInternalTraceCode = (dateReceived: string, supplierId: string, lotNumber: string): string => {
    if (!dateReceived || !supplierId || !lotNumber) return '';

    // Format date as YYYYMMDD - extract date portion directly to avoid timezone issues
    const datePortion = dateReceived.split('T')[0]; // Get "2026-01-13" from "2026-01-13" or "2026-01-13T..."
    const formattedDate = datePortion.replace(/-/g, ''); // Remove hyphens to get "20260113"

    // Get supplier code
    const supplier = suppliers.find(s => s.id === supplierId);
    const supplierCode = supplier?.supplierCode || 'UNKNOWN';

    return `${formattedDate}-${supplierCode}-${lotNumber}`;
  };

  // Handle supplier change
  const handleSupplierChange = (supplierId: string) => {
    onChange({
      ...entry,
      supplierId: supplierId,
      supplierLotNumber: '' // Clear lot number when supplier changes
    });
  };

  // Handle lot number change with auto-population
  const handleLotNumberChange = (lotNumber: string) => {
    if (!lotNumber) {
      // Clear auto-populated fields when lot number is cleared, but keep supplier
      onChange({
        ...entry,
        supplierLotNumber: '',
        ingredientReceivingId: undefined,
        internalTraceCode: ''
      });
      return;
    }

    // Find matching receiving entry
    const receivingEntry = shipmentEntries.find(
      se => se.lotNumber === lotNumber && se.supplierId === entry.supplierId
    );

    if (receivingEntry) {
      // Auto-populate fields from receiving entry
      const dateReceived = receivingEntry.receivingDate || receivingEntry.createdAt.split('T')[0];
      const internalTraceCode = generateInternalTraceCode(
        dateReceived,
        receivingEntry.supplierId,
        lotNumber
      );

      onChange({
        ...entry,
        supplierLotNumber: lotNumber,
        ingredientName: receivingEntry.ingredient,
        supplierId: receivingEntry.supplierId,
        dateReceived: dateReceived,
        ingredientReceivingId: receivingEntry.id,
        internalTraceCode: internalTraceCode
      });
    } else {
      // Just update lot number if no matching entry found
      onChange({
        ...entry,
        supplierLotNumber: lotNumber
      });
    }
  };

  // Handle quantity changes - store separately
  const handleQuantityNumberChange = (number: string) => {
    setQuantityNumber(number);
    onChange({ ...entry, quantityUsed: number || undefined, quantityUnit: quantityUnit || undefined });
  };

  const handleQuantityUnitChange = (unit: string) => {
    setQuantityUnit(unit);
    onChange({ ...entry, quantityUsed: quantityNumber || undefined, quantityUnit: unit || undefined });
  };

  // Handle finished product changes and combine before saving
  const handleProductNameChange = (product: string) => {
    setProductName(product);
    const combined = product && productModifier ? `${product} - ${productModifier}` : product;
    onChange({ ...entry, finishedProduct: combined });
  };

  const handleProductModifierChange = (modifier: string) => {
    setProductModifier(modifier);
    const combined = productName && modifier ? `${productName} - ${modifier}` : productName;
    onChange({ ...entry, finishedProduct: combined });
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  const cellStyle: React.CSSProperties = {
    padding: '8px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle'
  };

  const readOnlyStyle: React.CSSProperties = {
    ...cellStyle,
    color: '#333'
  };

  if (!isEditMode) {
    // View mode - display values
    return (
      <tr
        ref={rowRef}
        style={{
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: isHighlightActive ? 'rgba(33, 150, 243, 0.15)' : undefined,
          transition: 'background-color 0.3s ease-in-out',
          boxShadow: isHighlightActive ? 'inset 0 0 0 2px #2196F3' : undefined
        }}
      >
        <td id={`itc-${entry.id}-field-ingredientName`} style={readOnlyStyle}>{entry.ingredientName || '-'}</td>
        <td id={`itc-${entry.id}-field-supplierId`} style={readOnlyStyle}>{supplierName}</td>
        <td id={`itc-${entry.id}-field-supplierLotNumber`} style={readOnlyStyle}>
          {entry.supplierLotNumber ? (
            <ClickableIdentifier value={entry.supplierLotNumber} type="lotNumber" onClick={openTraceability} />
          ) : '-'}
        </td>
        <td id={`itc-${entry.id}-field-internalTraceCode`} style={readOnlyStyle}>
          {entry.internalTraceCode ? (
            <ClickableIdentifier value={entry.internalTraceCode} type="traceCode" onClick={openTraceability} />
          ) : '-'}
        </td>
        <td id={`itc-${entry.id}-field-dateUsed`} style={readOnlyStyle}>
          {usageFetched && usageRecords && usageRecords.length > 0 ? (() => {
            const dates = [...new Set(usageRecords.map(r => r.dateUsed).filter(Boolean).map(d => new Date(d).toLocaleDateString('en-US')))];
            if (dates.length === 0) return entry.dateUsed ? new Date(entry.dateUsed).toLocaleDateString('en-US') : '-';
            if (dates.length <= 2) return dates.join(', ');
            return `${dates[0]}, ${dates[1]} +${dates.length - 2} more`;
          })() : entry.dateUsed ? new Date(entry.dateUsed).toLocaleDateString('en-US') : '-'}
        </td>
        <td id={`itc-${entry.id}-field-quantityUsed`} style={readOnlyStyle}>
          {entry.quantityUsed && entry.quantityUnit ? `${entry.quantityUsed} ${entry.quantityUnit}` : entry.quantityUsed || '-'}
        </td>
        <td id={`itc-${entry.id}-field-finishedProduct`} style={readOnlyStyle}>
          {usageFetched && usageRecords && usageRecords.length > 0 ? (() => {
            const products = [...new Set(usageRecords.map(r => r.productName).filter(Boolean))];
            if (products.length === 0) return entry.finishedProduct || '-';
            if (products.length <= 2) return products.join(', ');
            return `${products[0]}, ${products[1]} +${products.length - 2} more`;
          })() : entry.finishedProduct || '-'}
        </td>
        <td id={`itc-${entry.id}-field-verifiedBy`} style={readOnlyStyle}>{entry.verifiedBy || '-'}</td>
        <td id={`itc-${entry.id}-field-comments`} style={readOnlyStyle}>{entry.comments || '-'}</td>
        <td style={readOnlyStyle}>
          {isUsageLoading ? (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Loading...</span>
          ) : usageCount !== undefined ? (
            <span
              style={{
                padding: '4px 8px',
                backgroundColor: usageCount > 0 ? '#e8f5e9' : '#f5f5f5',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                color: usageCount > 0 ? '#2e7d32' : '#999'
              }}
            >
              {usageCount === 0 ? 'Not used' : `Used in ${usageCount} batch${usageCount !== 1 ? 'es' : ''}`}
            </span>
          ) : (
            <span style={{ color: '#ccc', fontSize: '12px' }}>-</span>
          )}
        </td>
        <td style={cellStyle}></td>
      </tr>
    );
  }

  // Edit mode - display inputs
  return (
    <tr>
      {/* Ingredient Name */}
      <td style={cellStyle}>
        <input
          type="text"
          value={entry.ingredientName}
          onChange={e => onChange({ ...entry, ingredientName: e.target.value })}
          style={inputStyle}
          placeholder="Ingredient name"
        />
      </td>

      {/* Supplier */}
      <td style={cellStyle}>
        <select value={entry.supplierId} onChange={e => handleSupplierChange(e.target.value)} style={inputStyle}>
          <option value="">Select Supplier</option>
          {suppliers.length === 0 && (
            <option value="" disabled>
              (No suppliers available)
            </option>
          )}
          {suppliers.map(supplier => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.supplierName}
            </option>
          ))}
        </select>
      </td>

      {/* Supplier Lot # */}
      <td style={cellStyle}>
        <select
          value={entry.supplierLotNumber}
          onChange={e => handleLotNumberChange(e.target.value)}
          style={inputStyle}
          disabled={!entry.supplierId}
        >
          <option value="">Select Lot #</option>
          {!entry.supplierId && (
            <option value="" disabled>
              (Select supplier first)
            </option>
          )}
          {availableLotNumbers.length === 0 && entry.supplierId && (
            <option value="" disabled>
              (No lots available)
            </option>
          )}
          {availableLotNumbers.map(lot => (
            <option key={lot} value={lot}>
              {lot}
            </option>
          ))}
        </select>
      </td>

      {/* Internal Trace Code */}
      <td style={cellStyle}>
        <input
          type="text"
          value={entry.internalTraceCode}
          onChange={e => onChange({ ...entry, internalTraceCode: e.target.value })}
          style={inputStyle}
          placeholder="Trace code"
        />
      </td>

      {/* Date Used */}
      <td style={cellStyle}>
        {usageFetched && usageRecords && usageRecords.length > 0 ? (
          <div style={{ padding: '8px', color: '#666', fontSize: '13px' }}>
            {(() => {
              const dates = [...new Set(usageRecords.map(r => r.dateUsed).filter(Boolean).map(d => new Date(d).toLocaleDateString('en-US')))];
              return dates.length > 0 ? dates.join(', ') : '-';
            })()}
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Auto-populated from batch usage</div>
          </div>
        ) : (
          <input
            type="date"
            value={entry.dateUsed || ''}
            onChange={e => onChange({ ...entry, dateUsed: e.target.value })}
            style={inputStyle}
          />
        )}
      </td>

      {/* Quantity Used - Split into number and unit */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <input
            type="number"
            value={quantityNumber}
            onChange={e => handleQuantityNumberChange(e.target.value)}
            style={{ ...inputStyle, flex: '1' }}
            placeholder="0"
            min="0"
            step="0.01"
          />
          <input
            type="text"
            value={quantityUnit}
            onChange={e => handleQuantityUnitChange(e.target.value)}
            style={{ ...inputStyle, flex: '1' }}
            placeholder="Unit"
          />
        </div>
      </td>

      {/* Finished Product - Split into product and modifier */}
      <td style={cellStyle}>
        {usageFetched && usageRecords && usageRecords.length > 0 ? (
          <div style={{ padding: '8px', color: '#666', fontSize: '13px' }}>
            {(() => {
              const products = [...new Set(usageRecords.map(r => r.productName).filter(Boolean))];
              return products.length > 0 ? products.join(', ') : '-';
            })()}
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Auto-populated from batch usage</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <select
              value={productName}
              onChange={e => handleProductNameChange(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select Product</option>
              {allProducts.length === 0 && (
                <option value="" disabled>
                  (No products available)
                </option>
              )}
              {allProducts.map(product => (
                <option key={product.id} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={productModifier}
              onChange={e => handleProductModifierChange(e.target.value)}
              style={inputStyle}
              placeholder="Modifier (optional)"
            />
          </div>
        )}
      </td>

      {/* Verified By */}
      <td style={cellStyle}>
        <input
          type="text"
          value={entry.verifiedBy}
          onChange={e => onChange({ ...entry, verifiedBy: e.target.value })}
          style={inputStyle}
          placeholder="Name"
        />
      </td>

      {/* Comments */}
      <td style={cellStyle}>
        <input
          type="text"
          value={entry.comments}
          onChange={e => onChange({ ...entry, comments: e.target.value })}
          style={inputStyle}
          placeholder="Comments"
        />
      </td>

      {/* Actions (Delete) */}
      <td style={{ ...cellStyle, textAlign: 'center', width: '60px' }}>
        <button
          type="button"
          onClick={onDelete}
          style={{
            padding: '6px 10px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
          title="Delete entry"
        >
          ✕
        </button>
      </td>
    </tr>
  );
};
